import { useQuery } from '@tanstack/react-query';
import { STALE_TIME_LIST_MS } from '../../constants/reactQueryStaleTime.ts';
import type { ICareHomeListResponse } from '../../interfaces';
import type { IAccountListResponse } from '../../interfaces/Account/IAccountListResponse.ts';
import AccountService from '../../services/AccountService.ts';
import CareHomeService from '../../services/CareHomeService.ts';

export type AppAccountsListQueryInput = {
  page: number;
  perPage: number;
  query?: string;
  organisationId?: string;
  carehomeId?: string;
  isActive: boolean;
  orderBy?: string;
  sortOrder?: 'asc' | 'desc';
};

export const getAppAccountsListQueryKey = (input: AppAccountsListQueryInput) =>
  [
    'appAccounts',
    input.organisationId,
    input.carehomeId,
    input.isActive,
    input.page,
    input.perPage,
    input.query,
    input.orderBy,
    input.sortOrder,
  ] as const;

export function useAppAccountsListQuery(input: AppAccountsListQueryInput, enabled: boolean) {
  return useQuery<IAccountListResponse>({
    queryKey: getAppAccountsListQueryKey(input),
    enabled,
    staleTime: STALE_TIME_LIST_MS,
    queryFn: () =>
      AccountService.getAccounts(
        input.page,
        input.perPage,
        input.query,
        input.carehomeId,
        'Nursing professional',
        undefined,
        input.orderBy,
        input.sortOrder ?? 'desc',
        input.organisationId,
        input.isActive ? 'true' : 'false'
      ),
  });
}

export type ActiveAccountsCountQueryInput = {
  organisationId?: string;
  carehomeId?: string;
};

export const getActiveAppAccountsCountQueryKey = (input: ActiveAccountsCountQueryInput) =>
  ['activeAppAccountsCount', input.organisationId, input.carehomeId] as const;

export function useActiveAppAccountsCountQuery(input: ActiveAccountsCountQueryInput, enabled: boolean) {
  return useQuery<number>({
    queryKey: getActiveAppAccountsCountQueryKey(input),
    enabled,
    staleTime: STALE_TIME_LIST_MS,
    queryFn: () => AccountService.getActiveAccountsCount(input.organisationId, input.carehomeId, 'Nursing professional'),
  });
}

export const getCareHomesForAppAccountsQueryKey = (organisationId?: string) =>
  ['careHomesForAppAccountsFilters', organisationId] as const;

export function useCareHomesForAppAccountsQuery(organisationId?: string, enabled = true) {
  return useQuery<ICareHomeListResponse>({
    queryKey: getCareHomesForAppAccountsQueryKey(organisationId),
    enabled: enabled && !!organisationId,
    staleTime: STALE_TIME_LIST_MS,
    queryFn: () =>
      CareHomeService.getCareHomes({
        organisation_id: organisationId!,
      }),
  });
}

export const getCareHomeForAppAccountsQueryKey = (careHomeId?: string) =>
  ['careHome', careHomeId] as const;

export function useCareHomeForAppAccountsQuery(careHomeId?: string, enabled = true) {
  return useQuery({
    queryKey: getCareHomeForAppAccountsQueryKey(careHomeId),
    enabled: enabled && !!careHomeId,
    staleTime: STALE_TIME_LIST_MS,
    queryFn: () => CareHomeService.getCareHome(careHomeId!),
  });
}

